/**
 * MCP Device Authorization Page (RFC-016 Phase 2)
 *
 * Handles OAuth 2.1 Device Flow authorization for MCP clients.
 * Users visit this page to authorize AI assistants to access their flashcards.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthButton } from '../components/AuthButton';
import './McpAuthorize.css';

// MCP API URL - will be set via environment variable in production
const MCP_API_URL = import.meta.env.VITE_MCP_API_URL;

export function McpAuthorize() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated, idToken, signIn } = useAuth();

  const [userCode, setUserCode] = useState(searchParams.get('code') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Format user code with dash (XXXX-XXXX)
  const formatUserCode = (code: string): string => {
    const cleaned = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
  };

  // Handle user code input change
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatUserCode(e.target.value);
    setUserCode(formatted);
    setError(null);
  };

  // Auto-format code from URL parameter
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      const formatted = formatUserCode(codeFromUrl);
      setUserCode(formatted);
    }
  }, [searchParams]);

  // Handle approve action
  const handleApprove = async () => {
    if (!isAuthenticated || !idToken) {
      signIn();
      return;
    }

    if (!MCP_API_URL) {
      setError('MCP API not configured');
      return;
    }

    if (userCode.length !== 9) {
      setError('Please enter a valid 8-character code');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${MCP_API_URL}/auth/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_code: userCode,
          user_id: user?.id,
          access_token: idToken,
          refresh_token: localStorage.getItem('stc_auth')
            ? JSON.parse(localStorage.getItem('stc_auth')!).refreshToken
            : undefined,
          id_token: idToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'not_found') {
          setError('Code not found or expired. Please check and try again.');
        } else if (data.error === 'expired_token') {
          setError('This code has expired. Please request a new one from your AI assistant.');
        } else if (data.error === 'invalid_request') {
          setError(data.error_description || 'Invalid request');
        } else {
          setError(data.error_description || 'Authorization failed');
        }
        return;
      }

      setSuccess(
        'Authorization successful! You can now close this page and return to your AI assistant.'
      );
    } catch (err) {
      console.error('Approve error:', err);
      setError('Failed to authorize. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deny action
  const handleDeny = async () => {
    if (!MCP_API_URL) {
      setError('MCP API not configured');
      return;
    }

    if (userCode.length !== 9) {
      setError('Please enter a valid 8-character code');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${MCP_API_URL}/auth/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_code: userCode,
          deny: true,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error_description || 'Failed to deny authorization');
        return;
      }

      setSuccess('Authorization denied. You can close this page.');
    } catch (err) {
      console.error('Deny error:', err);
      setError('Failed to deny. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigate back to study
  const handleBackToStudy = () => {
    navigate('/');
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="mcp-authorize">
        <div className="mcp-container">
          <div className="mcp-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-authorize">
      <div className="mcp-container">
        <header className="mcp-header">
          <h1>Authorize AI Assistant</h1>
          <p className="mcp-subtitle">Connect your AI assistant to study flashcards</p>
        </header>

        {!MCP_API_URL && (
          <div className="mcp-error">MCP API is not configured. Please contact support.</div>
        )}

        {success ? (
          <div className="mcp-success-container">
            <div className="mcp-success-icon">&#10003;</div>
            <p className="mcp-success-message">{success}</p>
            <button className="mcp-button mcp-button-secondary" onClick={handleBackToStudy}>
              Back to Study
            </button>
          </div>
        ) : (
          <>
            {/* Auth status */}
            <div className="mcp-auth-section">
              <div className="mcp-auth-status">
                {isAuthenticated ? (
                  <div className="mcp-user-info">
                    <span className="mcp-user-icon">&#128100;</span>
                    <span>Signed in as {user?.email}</span>
                  </div>
                ) : (
                  <div className="mcp-sign-in-prompt">
                    <p>Sign in to authorize your AI assistant</p>
                    <AuthButton />
                  </div>
                )}
              </div>
            </div>

            {/* Code input */}
            <div className="mcp-code-section">
              <label htmlFor="user-code" className="mcp-label">
                Enter the code shown by your AI assistant:
              </label>
              <input
                id="user-code"
                type="text"
                className="mcp-code-input"
                value={userCode}
                onChange={handleCodeChange}
                placeholder="XXXX-XXXX"
                maxLength={9}
                disabled={isSubmitting || !!success}
              />
            </div>

            {error && <div className="mcp-error">{error}</div>}

            {/* Action buttons */}
            <div className="mcp-actions">
              <button
                className="mcp-button mcp-button-primary"
                onClick={handleApprove}
                disabled={!isAuthenticated || userCode.length !== 9 || isSubmitting}
              >
                {isSubmitting ? 'Authorizing...' : 'Authorize'}
              </button>
              <button
                className="mcp-button mcp-button-secondary"
                onClick={handleDeny}
                disabled={userCode.length !== 9 || isSubmitting}
              >
                Deny
              </button>
            </div>

            {/* Help text */}
            <div className="mcp-help">
              <h3>How this works</h3>
              <ol>
                <li>Your AI assistant shows you a code (like ABCD-1234)</li>
                <li>Enter that code above</li>
                <li>Click "Authorize" to grant access to your flashcards</li>
                <li>Return to your AI assistant to start studying</li>
              </ol>
              <p className="mcp-security-note">
                This authorization allows your AI assistant to view and rate your flashcards. You
                can revoke access at any time by signing out.
              </p>
            </div>

            {/* Back link */}
            <div className="mcp-back">
              <button className="mcp-link-button" onClick={handleBackToStudy}>
                &larr; Back to Study
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
